import { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Text } from "@react-three/drei";
import { Group, Mesh, MeshStandardMaterial } from "three";
import gsap from "gsap";
import { ObstacleNote } from "./beatmap";
import { useGameStore } from "./useGameStore";
import { SPAWN_Z, TRAVEL_SPEED } from "./constants";
import { pulseAllBlades } from "./collision";
import { DODGE_TARGET_SPEC } from "./targetSystem";

const DODGE_X = 0.18;
const DUCK_Y = 1.38;

const CHECK_Z_START = -0.55;
const CHECK_Z_END = 0.25;

const DANGER = DODGE_TARGET_SPEC.color;
const DANGER_DARK = DODGE_TARGET_SPEC.shadow;

interface Config {
  panelX: number;
  panelY: number;
  panelW: number;
  panelH: number;
  kanji: string;
  label: string;
}

const CONFIGS: Record<ObstacleNote["type"], Config> = {
  "dodge-left": {
    panelX: 0.52,
    panelY: 1.28,
    panelW: 0.68,
    panelH: 1.36,
    kanji: "左",
    label: "DODGE LEFT",
  },
  "dodge-right": {
    panelX: -0.52,
    panelY: 1.28,
    panelW: 0.68,
    panelH: 1.36,
    kanji: "右",
    label: "DODGE RIGHT",
  },
  duck: {
    panelX: 0.0,
    panelY: 1.66,
    panelW: 1.4,
    panelH: 0.36,
    kanji: "低",
    label: "DUCK",
  },
};

interface ObstacleProps {
  note: ObstacleNote;
  obstacleId: number;
  getCurrentTime: () => number;
  onRemove: () => void;
}

function BarrierShape({
  cfg,
  isDuck,
  opacity,
}: {
  cfg: Config;
  isDuck: boolean;
  opacity: number;
}) {
  return (
    <mesh
      position={[cfg.panelX, cfg.panelY, 0]}
      rotation={isDuck ? [0, 0, 0] : [Math.PI / 2, 0, Math.PI / 6]}
      scale={isDuck ? [1, 1, 1] : [cfg.panelW, cfg.panelH, 1]}
    >
      {isDuck ? (
        <boxGeometry args={[cfg.panelW, cfg.panelH, 0.07]} />
      ) : (
        <cylinderGeometry args={[0.5, 0.5, 0.08, 6]} />
      )}
      <meshStandardMaterial
        color={DANGER}
        emissive={DANGER}
        emissiveIntensity={isDuck ? 0.65 : 0.95}
        transparent
        opacity={opacity}
        depthWrite={false}
      />
    </mesh>
  );
}

function DuckFrame({ cfg }: { cfg: Config }) {
  return (
    <>
      <mesh position={[cfg.panelX, cfg.panelY + cfg.panelH / 2, 0.02]}>
        <boxGeometry args={[cfg.panelW + 0.025, 0.025, 0.07]} />
        <meshStandardMaterial
          color={DANGER}
          emissive={DANGER}
          emissiveIntensity={1.4}
        />
      </mesh>
      <mesh position={[cfg.panelX, cfg.panelY - cfg.panelH / 2, 0.02]}>
        <boxGeometry args={[cfg.panelW + 0.025, 0.025, 0.07]} />
        <meshStandardMaterial
          color={DANGER}
          emissive={DANGER}
          emissiveIntensity={1.4}
        />
      </mesh>
      <mesh position={[cfg.panelX - cfg.panelW / 2, cfg.panelY, 0.02]}>
        <boxGeometry args={[0.025, cfg.panelH, 0.07]} />
        <meshStandardMaterial
          color={DANGER}
          emissive={DANGER}
          emissiveIntensity={1.4}
        />
      </mesh>
      <mesh position={[cfg.panelX + cfg.panelW / 2, cfg.panelY, 0.02]}>
        <boxGeometry args={[0.025, cfg.panelH, 0.07]} />
        <meshStandardMaterial
          color={DANGER}
          emissive={DANGER}
          emissiveIntensity={1.4}
        />
      </mesh>
    </>
  );
}

export function Obstacle({
  note,
  obstacleId,
  getCurrentTime,
  onRemove,
}: ObstacleProps) {
  const groupRef = useRef<Group>(null);
  const panelRef = useRef<Mesh>(null);
  const spawnRef = useRef<number | null>(null);
  const checkedRef = useRef(false);
  const doneRef = useRef(false);
  const removeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { camera } = useThree();
  const phase = useGameStore((s) => s.phase);

  const cfg = CONFIGS[note.type];
  const isDuck = note.type === "duck";

  useEffect(() => {
    return () => {
      if (removeTimeoutRef.current != null)
        clearTimeout(removeTimeoutRef.current);
      if (panelRef.current) gsap.killTweensOf(panelRef.current.material);
    };
  }, []);

  function scheduleRemove(delayMs: number) {
    if (removeTimeoutRef.current != null)
      clearTimeout(removeTimeoutRef.current);
    removeTimeoutRef.current = setTimeout(() => {
      removeTimeoutRef.current = null;
      onRemove();
    }, delayMs);
  }

  function triggerHit() {
    if (doneRef.current) return;
    doneRef.current = true;
    useGameStore.getState().addMiss();
    useGameStore.getState().resetCombo();
    useGameStore.getState().recordObstacleEvent({
      id: obstacleId,
      t: note.t,
      type: note.type,
      result: "hit",
    });
    pulseAllBlades("OBSTACLE");

    if (panelRef.current) {
      const mat = panelRef.current.material as MeshStandardMaterial;
      gsap.to(mat, { emissiveIntensity: 3.0, opacity: 0.9, duration: 0.08 });
      gsap.to(mat, {
        emissiveIntensity: 0.0,
        opacity: 0.0,
        duration: 0.35,
        delay: 0.08,
      });
    }
    scheduleRemove(450);
  }

  function triggerSafe() {
    if (doneRef.current) return;
    doneRef.current = true;
    useGameStore.getState().recordObstacleEvent({
      id: obstacleId,
      t: note.t,
      type: note.type,
      result: "cleared",
    });
    if (panelRef.current) {
      const mat = panelRef.current.material as MeshStandardMaterial;
      gsap.to(mat, { opacity: 0.0, duration: 0.25 });
    }
    scheduleRemove(300);
  }

  useFrame(() => {
    if (phase !== "playing") return;
    if (!groupRef.current || doneRef.current) return;

    if (spawnRef.current === null) spawnRef.current = getCurrentTime();

    const z = SPAWN_Z + (getCurrentTime() - spawnRef.current) * TRAVEL_SPEED;
    groupRef.current.position.z = z;

    if (!checkedRef.current && z > CHECK_Z_START && z < CHECK_Z_END) {
      const cx = camera.position.x;
      const cy = camera.position.y;

      const hit =
        (note.type === "dodge-left" && cx > DODGE_X) ||
        (note.type === "dodge-right" && cx < -DODGE_X) ||
        (note.type === "duck" && cy > DUCK_Y);

      if (hit) {
        checkedRef.current = true;
        triggerHit();
        return;
      }
    }

    if (!checkedRef.current && z >= CHECK_Z_END) {
      checkedRef.current = true;
      triggerSafe();
    }
  });

  return (
    <group ref={groupRef} position={[0, 0, SPAWN_Z]}>
      <mesh
        ref={panelRef}
        position={[cfg.panelX, cfg.panelY, 0]}
        rotation={isDuck ? [0, 0, 0] : [Math.PI / 2, 0, Math.PI / 6]}
        scale={isDuck ? [1, 1, 1] : [cfg.panelW, cfg.panelH, 1]}
      >
        {isDuck ? (
          <boxGeometry args={[cfg.panelW, cfg.panelH, 0.07]} />
        ) : (
          <cylinderGeometry args={[0.5, 0.5, 0.08, 6]} />
        )}
        <meshStandardMaterial
          color={DANGER}
          emissive={DANGER}
          emissiveIntensity={0.75}
          transparent
          opacity={0.42}
          depthWrite={false}
        />
      </mesh>

      {!isDuck && (
        <>
          <BarrierShape cfg={cfg} isDuck={false} opacity={0.16} />
          <mesh
            position={[cfg.panelX, cfg.panelY, 0.06]}
            rotation={[Math.PI / 2, 0, Math.PI / 6]}
            scale={[cfg.panelW * 1.08, cfg.panelH * 1.08, 1]}
          >
            <cylinderGeometry args={[0.5, 0.5, 0.018, 6]} />
            <meshStandardMaterial
              color={DANGER}
              emissive={DANGER}
              emissiveIntensity={1.85}
              transparent
              opacity={0.24}
              depthWrite={false}
            />
          </mesh>
          <mesh
            position={[cfg.panelX, cfg.panelY, 0.075]}
            rotation={[Math.PI / 2, 0, Math.PI / 6]}
            scale={[cfg.panelW * 0.76, cfg.panelH * 0.76, 1]}
          >
            <cylinderGeometry args={[0.5, 0.5, 0.016, 6]} />
            <meshStandardMaterial
              color={DANGER_DARK}
              emissive={DANGER_DARK}
              emissiveIntensity={0.7}
              transparent
              opacity={0.35}
              depthWrite={false}
            />
          </mesh>
        </>
      )}

      {isDuck && <DuckFrame cfg={cfg} />}

      <Text
        position={[cfg.panelX, cfg.panelY + (isDuck ? 0.035 : 0.08), 0.11]}
        fontSize={isDuck ? 0.105 : 0.18}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.012}
        outlineColor={DANGER_DARK}
      >
        {cfg.kanji}
      </Text>
      <Text
        position={[cfg.panelX, cfg.panelY - (isDuck ? 0.085 : 0.12), 0.115]}
        fontSize={isDuck ? 0.052 : 0.056}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.007}
        outlineColor={DANGER_DARK}
        letterSpacing={0.04}
        maxWidth={cfg.panelW * 0.78}
        textAlign="center"
      >
        {cfg.label}
      </Text>

      <pointLight
        position={[cfg.panelX, cfg.panelY, 0.4]}
        color={DANGER}
        intensity={1.05}
        distance={1.35}
      />
    </group>
  );
}
